/* global Office */

import deMessages from "./messages/de";
import enMessages from "./messages/en";

export const LOCALES = {
  DE: "de-DE",
  EN: "en-US",
};

export const LOCALE_STORAGE_KEY = "avamboo_locale_override";

const catalogs = {
  [LOCALES.DE]: deMessages,
  [LOCALES.EN]: enMessages,
};

export function normalizeLocale(locale) {
  if (!locale || typeof locale !== "string") return LOCALES.EN;
  return locale.toLowerCase().startsWith("de") ? LOCALES.DE : LOCALES.EN;
}

export function getStoredLocaleOverride() {
  try {
    const value = localStorage.getItem(LOCALE_STORAGE_KEY);
    return value ? normalizeLocale(value) : null;
  } catch {
    return null;
  }
}

export function setStoredLocaleOverride(locale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalizeLocale(locale));
  } catch {
    // Ignore localStorage issues in Office WebViews.
  }
}

export function detectLocale() {
  try {
    if (typeof Office !== "undefined" && Office?.context?.displayLanguage) {
      return normalizeLocale(Office.context.displayLanguage);
    }
  } catch {
    // Office may not be ready in tests.
  }

  if (typeof navigator !== "undefined") {
    if (navigator.language) {
      return normalizeLocale(navigator.language);
    }
    if (navigator.languages?.length) {
      return normalizeLocale(navigator.languages[0]);
    }
  }

  return LOCALES.EN;
}

export function getCurrentLocale() {
  return getStoredLocaleOverride() || detectLocale();
}

export function getMessages(locale = getCurrentLocale()) {
  return catalogs[normalizeLocale(locale)] || {};
}

export function formatMessage(template, params = {}) {
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    return value == null ? `{${key}}` : String(value);
  });
}

export function translate(key, params = {}, locale = getCurrentLocale()) {
  const template = getMessages(locale)[key] || key;
  return params && Object.keys(params).length > 0
    ? formatMessage(template, params)
    : template;
}

export function createTranslator(locale) {
  return (key, params) => translate(key, params, locale);
}

export function formatDate(value, options = {}, locale = getCurrentLocale()) {
  const parsedValue = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsedValue.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat(normalizeLocale(locale), options).format(
    parsedValue,
  );
}

export function formatTime(value, options = {}, locale = getCurrentLocale()) {
  return formatDate(value, options, locale);
}

export function formatDateTime(value, options = {}, locale = getCurrentLocale()) {
  return formatDate(value, options, locale);
}
